import React, { useState, useRef, useEffect } from "react";
import base64 from "base-64";
import Parse from "papaparse";
import Papa from "papaparse";
import { services } from "../services";
import { IoMdClose, IoMdInformationCircleOutline } from "react-icons/io";
import DataTable from "react-data-table-component";
import { FiRefreshCw } from "react-icons/fi";
import { useMemo } from "react";
import { jwtDecode } from "jwt-decode";
import { getCookieObject } from "../utils/cookie";

const validators = {
  name: (val) => val && /^[A-Za-z\s]+$/.test(val.trim()),
  mobile: (val) => /^\d{10}$/.test(val),
  email: (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
  dob: (val) => /^\d{2}\/\d{2}\/\d{4}$/.test(val),
  pincode: (val) => /^\d{6}$/.test(val),
};

const UserBulkUpload = () => {
  const [selectedPartner, setSelectedPartner] = useState("");
  const [user, setUsers] = useState([]);
  const [columns, setColumns] = useState([]);
  const [files, setFiles] = useState({});
  const [errorMessages, setErrorMessages] = useState([]);
  const [csvFileName, setCsvFileName] = useState("");
  const [onboardingStatusPerPartner, setOnboardingStatusPerPartner] = useState(
    [],
  );
  const [partnersList, setPartnersList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [skipDocuments, setSkipDocuments] = useState(false);
  const [fileErrors, setFileErrors] = useState({});
  const csvFileInputRef = useRef(null);
  const documentRefs = useRef({});

  const decodedToken = useMemo(() => {
    const { access_token: accessToken } = getCookieObject();
    if (!accessToken) return null;
    try {
      return jwtDecode(accessToken);
    } catch (err) {
      console.error("Invalid token", err);
      return null;
    }
  }, []);

  const candidatePartnerId = decodedToken?.partnerId;

  const handleFileChange = (rowIndex, type, e) => {
    const file = e.target.files[0];
    if (file && file.size >= 4 * 1024 * 1024) {
      setFileErrors((prev) => ({
        ...prev,
        [rowIndex]: {
          ...prev[rowIndex],
          [type]: `File size exceeds ${4}MB`,
        },
      }));
    } else if (file && file.size <= 100 * 1024) {
      setFileErrors((prev) => ({
        ...prev,
        [rowIndex]: {
          ...prev[rowIndex],
          [type]: `File size is less than ${100}kb`,
        },
      }));
    } else {
      setFileErrors((prev) => ({
        ...prev,
        [rowIndex]: {
          ...prev[rowIndex],
          [type]: "",
        },
      }));
    }
    setFiles((prev) => ({
      ...prev,
      [rowIndex]: {
        ...prev[rowIndex],
        [type]: file,
      },
    }));
  };
  const allowedValues = {
    salutation: ["Mr", "Mrs", "Ms"],
    gender: ["Male", "Female", "Other"],
    category: ["General", "OBC", "SC", "ST"],
    identityProofType: [
      "Employer Card",
      "PAN Card",
      "Passport",
      "Driving License",
    ],
    highestQualification: [
      "10th Standard",
      "12th Standard",
      "Graduate",
      "Post Graduate",
    ],
  };
  const validateRow = (row, index) => {
    const issues = [];
    const fieldMap = {
      salutation: 0,
      name: 1,
      mobile: 2,
      email: 3,
      dob: 4,
      pincode: 10,
      gender: 5,
      category: 6,
      identityProofType: 12,
      highestQualification: 11,
    };
    if (row.some((cell) => !cell || cell.trim() === "")) {
      issues.push("Missing required fields");
    }
    const name = row[fieldMap.name]?.trim() || "";
    const mobile = row[fieldMap.mobile]?.trim() || "";
    const email = row[fieldMap.email]?.trim() || "";
    const dob = row[fieldMap.dob]?.trim() || "";
    const gender = row[fieldMap.gender]?.trim() || "";
    const category = row[fieldMap.category]?.trim() || "";
    const idType = row[fieldMap.identityProofType]?.trim() || "";
    const qualification = row[fieldMap.highestQualification]?.trim() || "";
    const salutation = row[fieldMap.salutation]?.trim() || "";
    const pincode = row[fieldMap.pincode]?.trim() || "";

    if (!validators.name(name)) issues.push("Invalid Name");
    if (!validators.mobile(mobile)) issues.push("Invalid Mobile Number");
    if (!validators.email(email)) issues.push("Invalid Email");
    if (!validators.dob(dob)) issues.push("Invalid DOB (dd/mm/yyyy)");
    if (!validators.pincode(pincode)) issues.push("Invalid Pincode");
    const matchIgnoreCase = (val, options) =>
      options.some((opt) => opt.toLowerCase() === val.toLowerCase());

    if (!matchIgnoreCase(gender, allowedValues.gender)) {
      issues.push(
        `Invalid Gender: "${gender}". Allowed values are: ${allowedValues.gender.join(
          ", ",
        )}`,
      );
    }

    if (!matchIgnoreCase(category, allowedValues.category)) {
      issues.push(
        `Invalid Category: "${category}". Allowed values are: ${allowedValues.category.join(
          ", ",
        )}`,
      );
    }

    if (!matchIgnoreCase(idType, allowedValues.identityProofType)) {
      issues.push(
        `Invalid Identity Proof Type: "${idType}". Allowed values are: ${allowedValues.identityProofType.join(
          ", ",
        )}`,
      );
    }

    if (!matchIgnoreCase(qualification, allowedValues.highestQualification)) {
      issues.push(
        `Invalid Qualification: "${qualification}". Allowed values are: ${allowedValues.highestQualification.join(
          ", ",
        )}`,
      );
    }
    if (!matchIgnoreCase(salutation, allowedValues.salutation)) {
      issues.push(
        `Invalid Salutation: "${salutation}". Allowed values are: ${allowedValues.salutation.join(
          ", ",
        )}`,
      );
    }

    return issues.length ? `Row ${index + 2}: ${issues.join(", ")}` : null;
  };

  const processCsvData = ({ data = [] }) => {
    let labels = data[0];
    const serialNumber = "S.No";
    labels = [serialNumber, ...labels];
    const rows = data
      .slice(1)
      .filter((row) => row.some((val) => val?.trim() !== ""));
    const errors = rows
      .map((row, idx) => validateRow(row, idx))
      .filter(Boolean);

    if (errors.length) {
      setErrorMessages(errors);
      setUsers([]);
    } else {
      setColumns(labels);
      setUsers(rows);
      setErrorMessages([]);
    }
  };

  const changeFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = (output) => {
      const data = output.target.result.split(",")[1];
      const decodedString = base64.decode(data);
      Parse.parse(decodedString, { complete: processCsvData });
    };
    reader.readAsDataURL(file);
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await services.requestSvc.post("/upload-file", formData);
    return response?.id;
  };
  const handleSubmit = async () => {
    setIsLoading(true);
    setIsSuccess(false);
    const uploadResults = [];
    for (let i = 0; i < user.length; i++) {
      const row = user[i];
      const fileGroup = files[i] || {};
      const identifier = row[3];
      if (!skipDocuments) {
        // Documents mandatory — check all four are present
        if (
          !fileGroup.certificate ||
          !fileGroup.idProof ||
          !fileGroup.photo ||
          !fileGroup.signature
        ) {
          alert(`Row ${i + 1} is missing one or more documents.`);
          setIsLoading(false);
          return;
        }
        // Check no client-side type errors
        if (
          fileErrors[i]?.certificate ||
          fileErrors[i]?.idProof ||
          fileErrors[i]?.photo ||
          fileErrors[i]?.signature
        ) {
          alert(
            `Please fix the file errors for row ${i + 1} before submitting.`,
          );
          setIsLoading(false);
          return;
        }
      }
      try {
        // [TEMP - RE-ENABLE AFTER 2-3 DAYS] Mandatory file uploads
        // const [certificateId, idProofId, photoId, signatureId] =
        //   await Promise.all([
        //     uploadFile(fileGroup.certificate),
        //     uploadFile(fileGroup.idProof),
        //     uploadFile(fileGroup.photo),
        //     uploadFile(fileGroup.signature),
        //   ]);
        const [certificateId, idProofId, photoId, signatureId] =
          await Promise.all([
            fileGroup.certificate
              ? uploadFile(fileGroup.certificate)
              : Promise.resolve(null),
            fileGroup.idProof
              ? uploadFile(fileGroup.idProof)
              : Promise.resolve(null),
            fileGroup.photo
              ? uploadFile(fileGroup.photo)
              : Promise.resolve(null),
            fileGroup.signature
              ? uploadFile(fileGroup.signature)
              : Promise.resolve(null),
          ]);
        uploadResults.push({
          rowIndex: i,
          identifier,
          documentIds: {
            certificateId,
            idProofId,
            photoId,
            signatureId,
          },
        });
      } catch (err) {
        console.log(err);
        setIsLoading(false);
        return;
      }
    }

    const salutationMap = {
      mr: "mr",
      mrs: "mrs",
      ms: "miss",
    };
    const genderMap = {
      male: "male",
      female: "female",
      other: "other",
    };
    const categoryMap = {
      general: "general",
      sc: "sc",
      st: "st",
      obc: "obc",
    };
    const qualificationMap = {
      "10th standard": "q_10",
      "12th standard": "q_12",
      graduate: "q_graduate",
      "post graduate": "q_post_graduate",
    };
    const identityProofMap = {
      "driving license": "driving_license",
      "employer card": "employers_card",
      "pan card": "pan",
      passport: "passport",
    };
    const normalize = (val) => val?.toString().trim().toLowerCase();

    const userPayloads = user.map((row, index) => {
      const salutation = salutationMap[normalize(row[0])];
      const name = row[1].trim();
      const email = row[3].trim();
      const mobileNumber = row[2].trim();
      const dob = row[4].trim();
      const [day, month, year] = dob.split("/");
      const formattedDob = `${year}-${month}-${day}`;
      const gender = genderMap[normalize(row[5])];
      const category = categoryMap[normalize(row[6])];
      const fatherName = row[7].trim().toUpperCase();
      const isPhysicallyChallenged =
        normalize(row[8]) === "true" || normalize(row[8]) === "yes";
      const highestQualification = qualificationMap[normalize(row[11])];
      const addressLine1 = row[9].trim().toUpperCase();
      const pincode = row[10].trim();
      const identityProofType = identityProofMap[normalize(row[12])];
      const identityProofNumber = row[13].trim().toUpperCase();

      const { idProofId, certificateId, photoId, signatureId } =
        uploadResults[index]?.documentIds || {};
      return {
        name,
        salutation,
        email,
        mobileNumber,
        gender,
        dateOfBirth: formattedDob + "T00:00:00.000Z",
        category,
        fatherName,
        isPhysicallyChallenged,
        highestQualification,
        addressLine1,
        pincode,
        identityProofType,
        identityProofNumber,
        // [TEMP - RE-ENABLE AFTER 2-3 DAYS] Replace below 4 spread lines with these original lines:
        // identityProofFileId: idProofId,
        // highestQualificationCertificateFileId: certificateId,
        // candidatePhotoFileId: photoId,
        // candidateSignatureFileId: signatureId,
        ...(idProofId && { identityProofFileId: idProofId }),
        ...(certificateId && {
          highestQualificationCertificateFileId: certificateId,
        }),
        ...(photoId && { candidatePhotoFileId: photoId }),
        ...(signatureId && { candidateSignatureFileId: signatureId }),
        partnerId: candidatePartnerId ? candidatePartnerId : selectedPartner,
      };
    });

    const csvContent = generateCsv(userPayloads);
    const previewTable = document.getElementById("preview-table");
    if (previewTable) previewTable.style.display = "none";
    setSelectedPartner("");
    csvFileInputRef.current.value = "";
    try {
      const fileId = await uploadCsvToS3(csvContent);
      console.log("CSV uploaded successfully. File ID:", fileId);
      const response = await services.requestSvc.post(
        "/process-bulk-onboarding",
        {
          dumpFileId: fileId,
          partnerId: selectedPartner,
        },
      );
      setIsSuccess(true);
    } catch (error) {
      console.error("Error uploading CSV to S3:", error);
      setIsSuccess(null);
    } finally {
      setIsLoading(false);
      fetchCsvStausData();
    }
  };
  const popoverRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);
  const renderLoader = () => {
    return (
      <div className="absolute inset-0 bg-gray-900 bg-opacity-70 flex justify-center items-center z-50">
        <div className="bg-white p-6 rounded-xl shadow-xl max-w-sm w-full border border-gray-200 text-center">
          <p className="text-gray-800 text-base font-medium">
            Uploading your documents...
            <br />
            This may take a few moments. Please do not close or refresh the
            page.
          </p>
        </div>
      </div>
    );
  };
  const renderModal = () => {
    return (
      <div className="absolute inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
        <div className="bg-white p-6 rounded-xl shadow-2xl w-96 max-w-full text-center border border-gray-200 flex flex-col items-center">
          <h2
            className={`text-lg font-semibold mb-4 ${
              isSuccess ? "text-green-600" : "text-red-600"
            }`}
          >
            {isSuccess ? "CSV Submitted Successfully!" : "Something Went Wrong"}
          </h2>

          {isSuccess ? (
            <>
              <p className="text-gray-700 text-sm">
                Your request is being processed and may take up to{" "}
                <span className="text-blue-600 font-medium">10 minutes</span>.
              </p>
              <div className="flex justify-center items-center gap-2 mt-4 text-gray-700 text-sm">
                <span>Click</span>
                <FiRefreshCw className="text-blue-500 mt-0.5" />
                <span>to refresh CSV status.</span>
              </div>
            </>
          ) : (
            <p className="text-gray-700 text-sm mt-2">
              Please try again or contact support if the issue persists.
            </p>
          )}

          <button
            onClick={() => {
              setIsSuccess(null);
              window.location.reload();
            }}
            className="mt-6 border text-black font-medium py-2 px-6 rounded hover:bg-blue-50"
          >
            OK
          </button>
        </div>
      </div>
    );
  };

  const clearCsvFile = () => {
    setCsvFileName("");
    if (csvFileInputRef.current) {
      csvFileInputRef.current.value = "";
    }
    setUsers([]);
    setColumns([]);
    setErrorMessages([]);
  };
  const clearFile = (rowIndex, type) => {
    const refKey = `${rowIndex}_${type}`;
    if (documentRefs.current[refKey]?.current) {
      documentRefs.current[refKey].current.value = "";
    }
    setFiles((prev) => {
      const updatedRow = { ...(prev[rowIndex] || {}) };
      delete updatedRow[type];
      return { ...prev, [rowIndex]: updatedRow };
    });
    setFileErrors((prev) => ({
      ...prev,
      [rowIndex]: {
        ...prev[rowIndex],
        [type]: "",
      },
    }));
  };
  const renderInstructions = () => {
    return (
      <>
        <div className="p-2 rounded-md border mt-2 w-1/4">
          <div
            className="flex gap-2 items-center justify-center cursor-pointer"
            onClick={() => setIsOpen((prev) => !prev)}
          >
            <p className="font-semibold text-md p-1">CSV Column Guidelines</p>
            <IoMdInformationCircleOutline
              size="20px"
              className="text-blue-500 mr-2 cursor-pointer"
            />
          </div>
        </div>
        {isOpen && (
          <div
            ref={popoverRef}
            className="absolute top-14 right-1 text-sm text-white-700 mt-10 rounded p-4 bg-[#f5fafa] border border-solid"
          >
            <div className="flex justify-between">
              <h1 className="text-md text-red-500">
                {" "}
                Below are the valid Values and formats that can be entered in
                CSV
              </h1>
              <IoMdClose
                size="20px"
                className="cursor-pointer text-gray-700 hover:text-red-500"
                onClick={() => setIsOpen(false)}
              />
            </div>

            <ul>
              <li className="flex gap-4">
                <strong className="text-[#2A6EC6]">Salutation:</strong> Mr, Mrs,
                Ms
              </li>
              <li className="flex gap-3">
                <strong className="text-[#2A6EC6]">Mobile Number:</strong>{" "}
                10-digit number
              </li>
              <li className="flex gap-3">
                <strong className="text-[#2A6EC6]">Email:</strong> Valid email
                address (abc@gmail.com)
              </li>
              <li className="flex gap-3">
                <strong className="text-[#2A6EC6]">DOB:</strong> DD/MM/YYYY
                format
              </li>
              <li className="flex gap-3">
                <strong className="text-[#2A6EC6]">Gender:</strong> Male,
                Female, Other
              </li>
              <li className="flex gap-3">
                <strong className="text-[#2A6EC6]">Category:</strong> General,
                OBC, SC, ST
              </li>
              <li className="flex gap-3">
                <strong className="text-[#2A6EC6]">Identity Proof:</strong>{" "}
                Employer Card, PAN Card, Passport, Driving License
              </li>
              <li className="flex gap-3">
                <strong className="text-[#2A6EC6]">
                  Highest Qualification:
                </strong>{" "}
                10th Standard, 12th Standard, Graduate, Post Graduate
              </li>
            </ul>
          </div>
        )}
      </>
    );
  };
  const generateCsv = (usersData) => {
    const headers = [
      "salutation",
      "name",
      "mobileNumber",
      "email",
      "dateOfBirth",
      "gender",
      "category",
      "fatherName",
      "isPhysicallyChallenged",
      "highestQualification",
      "addressLine1",
      "pincode",
      "identityProofType",
      "identityProofNumber",
      "highestQualificationCertificateFileId",
      "identityProofFileId",
      "candidatePhotoFileId",
      "candidateSignatureFileId",
      "partnerId",
    ];

    const rows = usersData.map((user) => [
      user.salutation,
      user.name,
      user.mobileNumber,
      user.email,
      user.dateOfBirth,
      user.gender,
      user.category,
      user.fatherName,
      user.isPhysicallyChallenged,
      user.highestQualification,
      user.addressLine1,
      user.pincode,
      user.identityProofType,
      user.identityProofNumber,
      user.highestQualificationCertificateFileId,
      user.identityProofFileId,
      user.candidatePhotoFileId,
      user.candidateSignatureFileId,
      user.partnerId,
    ]);

    const csv = Papa.unparse({
      fields: headers,
      data: rows,
    });

    return csv;
  };
  const uploadCsvToS3 = async (csvContent) => {
    const formData = new FormData();
    const timestamp = new Date()
      .toISOString()
      .replace(/[-:]/g, "")
      .replace(/\..+/, "");
    const fileName = `users_bulk_upload_${timestamp}.csv`;

    const blob = new Blob([csvContent], { type: "text/csv" });
    const file = new File([blob], fileName);

    formData.append("file", file);
    const response = await services.requestSvc.post("/upload-file", formData);
    return response?.id;
  };
  const fetchPartnersList = async () => {
    try {
      const res = await services.requestSvc.get("/partners");
      setPartnersList(res);
    } catch (error) {
      console.error("Error fetching partners list", error);
    }
  };
  const fetchCsvStausData = async () => {
    try {
      setIsRefreshing(true);
      const params = candidatePartnerId
        ? `?partnerId=${candidatePartnerId}`
        : "";
      const res = await services.requestSvc.get(`/csv-status${params}`);
      setOnboardingStatusPerPartner(res);
    } catch (error) {
      console.error("Error fetching csv status", error);
    } finally {
      setIsRefreshing(false);
    }
  };
  useEffect(() => {
    fetchPartnersList();
  }, []);
  useEffect(() => {
    fetchCsvStausData();
  }, []);
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      const message =
        "Do you really want to refresh the page? All uploaded data will be lost.";
      event.returnValue = message;
      return message;
    };
    if (isSuccess == false) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isSuccess]);
  const CsvStatusColumns = [
    {
      name: "CSV ID",
      selector: (row) => row.id,
    },
    {
      name: "Created At",
      selector: (row) => new Date(row.createdAt).toLocaleDateString(),
      width: "8rem",
    },
    {
      name: "Status",
      selector: (row) => row.processingStatus,
    },
    {
      name: "Created Count",
      selector: (row) => row.createdCount,
    },
    {
      name: "Duplicate Count",
      selector: (row) => row.duplicateCount,
    },
    {
      name: "Failed Count",
      selector: (row) => row.failedCount,
    },
    {
      name: "Error Details",
      cell: (row) => {
        if (!row.errorData || row.errorData.length === 0) {
          return <span className="text-gray-400 italic">No errors</span>;
        }

        let errors = [];
        try {
          errors =
            typeof row.errorData === "string"
              ? JSON.parse(row.errorData)
              : row.errorData;
        } catch (e) {
          console.error("Error parsing errorData", e);
        }

        return (
          <details className="cursor-pointer">
            <summary className="text-red-500 underline">
              View Errors ({errors.length})
            </summary>
            <ul className="mt-2 list-disc list-inside text-sm text-gray-700">
              {errors.map((err, idx) => (
                <li key={idx}>
                  <strong>{err.input?.name || "Unknown User"}:</strong>{" "}
                  {err.message}
                </li>
              ))}
            </ul>
          </details>
        );
      },
      wrap: true,
    },
    {
      name: "Partner Name",
      selector: (row) => row.partner.name,
    },
  ];

  return (
    <div className="p-4">
      {isLoading && renderLoader()}
      {isSuccess == true && renderModal()}
      <div className="mt-5 text-base text-gray-700">
        <strong className="text-xl text-black">
          Bulk Upload Users via CSV
        </strong>
        <br />
        <span className="text-sm text-gray-600">
          Please use the CSV format below to upload users in bulk. Make sure to
          follow the template structure and review the Csv Columns Guidelines
          carefully.
        </span>
      </div>

      <div className="flex gap-10">
        <a
          href="https://iibf-uploads-prod.s3.ap-south-1.amazonaws.com/Bulk_User_Upload_Template_CSV_with_sampleData.csv"
          target="_blank"
        >
          <button
            className="mt-3 btn-primary text-white rounded"
            style={{
              padding: "8px",
            }}
          >
            Download Template
          </button>
        </a>
        {renderInstructions()}
      </div>
      {candidatePartnerId && (
        <div className="mt-4 text-red-600 font-bold">
          IMPORTANT NOTICE: Do not upload any candidate data without explicit authorization from CSC Academy. For further details and permissions, reach out to Shahrukh (Mobile: 9971409665).
        </div>
      )}
      <hr className="mt-8 w-1/2" />
      <div className="mt-3">
        <div className="flex gap-20">
          {!candidatePartnerId && (
            <div className="mt-4">
              <p className="text-lg font-medium font-semibold">
                Select Partner
              </p>
              <select
                className="mt-2 p-2 border rounded"
                value={selectedPartner}
                onChange={(e) => setSelectedPartner(e.target.value)}
              >
                <option value="">-- Select a Partner --</option>
                {partnersList.map((partner) => (
                  <option key={partner.id} value={partner.id}>
                    {partner.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="mt-5">
            <p className="text-lg font-medium font-semibold">Upload CSV</p>

            <input
              ref={csvFileInputRef}
              type="file"
              accept=".csv"
              onInput={changeFile}
              disabled={candidatePartnerId ? false : !selectedPartner}
            />
            {csvFileName && (
              <button
                className="text-sm px-3 py-1 rounded border"
                onClick={clearCsvFile}
              >
                Clear
              </button>
            )}

            {!candidatePartnerId && !selectedPartner && (
              <p className="text-red-500 text-sm">
                Please select a partner before uploading CSV.
              </p>
            )}
          </div>
        </div>
      </div>
      {errorMessages.length > 0 && (
        <div className="mt-5 bg-red-100 border border-red-400 text-red-700 p-3 rounded">
          <h3 className="font-semibold mb-1">Validation Errors:</h3>
          <p className="text-sm mb-2">
            Clear current Uploaded CSV then Resolve errors and upload again.
          </p>
          <ul className="list-disc list-inside text-sm">
            {errorMessages.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        </div>
      )}
      <div id="preview-table">
        {user.length > 0 && (
          <div>
            <div className="mt-5 flex items-center gap-3 p-3 bg-yellow-50 border border-yellow-300 rounded w-fit">
              <input
                id="skip-docs"
                type="checkbox"
                checked={skipDocuments}
                onChange={(e) => setSkipDocuments(e.target.checked)}
                className="w-4 h-4 accent-yellow-500 cursor-pointer"
              />
              <label
                htmlFor="skip-docs"
                className="text-sm font-medium text-yellow-800 cursor-pointer select-none"
              >
                Skip document upload{" "}
                <span className="font-normal text-yellow-700">
                  (users will be created without documents)
                </span>
              </label>
            </div>
            <div className="overflow-auto mt-5">
              <table className="min-w-full border text-sm">
                <thead>
                  <tr>
                    {columns.map((label, i) => (
                      <th
                        key={i}
                        className={`border px-2 py-1 font-semibold bg-gray-100 
                              ${
                                label == "Name"
                                  ? "border sticky left-10 z-20 min-w-[8rem]"
                                  : label == "S.No"
                                    ? "border sticky left-0 z-20 w-10 min-w-[2.5rem]"
                                    : ""
                              } `}
                      >
                        {label}
                      </th>
                    ))}

                    {!skipDocuments &&
                      ["Certificate", "ID Proof", "Photo", "Signature"].map(
                        (label) => (
                          <th
                            key={label}
                            className="border px-2 py-1 font-semibold bg-gray-100"
                          >
                            {label}
                          </th>
                        ),
                      )}
                  </tr>
                </thead>
                <tbody>
                  {user.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      <td className="sticky left-0 border py-1 bg-gray-100 z-10 w-10 min-w-[2.5rem]">
                        {rowIndex + 1}
                      </td>
                      {row.map((cell, i) => (
                        <td
                          key={i}
                          className={`border px-2 py-1 
                         ${
                           columns[i + 1] === "Name"
                             ? "sticky left-10 border px-2 py-1 bg-gray-100 z-10 min-w-[8rem]"
                             : ""
                         } `}
                        >
                          {cell}
                        </td>
                      ))}
                      {!skipDocuments &&
                        ["certificate", "idProof", "photo", "signature"].map(
                          (docType) => {
                            const refKey = `${rowIndex}_${docType}`;
                            if (!documentRefs.current[refKey]) {
                              documentRefs.current[refKey] = React.createRef();
                            }

                            return (
                              <td key={docType} className="border px-2 py-1">
                                <div className="flex gap-1 items-center">
                                  <input
                                    ref={documentRefs.current[refKey]}
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.pdf"
                                    onChange={(e) =>
                                      handleFileChange(rowIndex, docType, e)
                                    }
                                  />

                                  {files[rowIndex]?.[docType] && (
                                    <button
                                      className="text-xs bg-red-500 text-white px-2 py-0.5 rounded"
                                      onClick={() =>
                                        clearFile(rowIndex, docType)
                                      }
                                    >
                                      Clear
                                    </button>
                                  )}
                                  {fileErrors[rowIndex]?.[docType] && (
                                    <span className="text-red-500 text-xs ml-2">
                                      {fileErrors[rowIndex][docType]}
                                    </span>
                                  )}
                                </div>
                              </td>
                            );
                          },
                        )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              onClick={handleSubmit}
              className="mt-4 btn-primary px-4 py-2 rounded"
            >
              Confirm and Create Users
            </button>
          </div>
        )}
      </div>
      {onboardingStatusPerPartner && (
        <div className="mt-20">
          <div className="flex items-center gap-2 cursor-pointer">
            <h1 className="font-semibold text-lg">CSV Status </h1>
            <span className="flex">
              <FiRefreshCw
                onClick={fetchCsvStausData}
                className={`transition-transform duration-500 ${
                  isRefreshing ? "animate-spin text-red-100 bg-red-200" : ""
                }`}
              />
              <div>{isRefreshing ? "..............." : ""}</div>
            </span>
          </div>

          <DataTable
            columns={CsvStatusColumns}
            data={onboardingStatusPerPartner || []}
            pagination
            paginationRowsPerPageOptions={[1, 10, 50, 100]}
            paginationPerPage={10}
            paginationTotalRows={onboardingStatusPerPartner?.length}
            customStyles={{
              table: {
                style: {
                  overflow: "hidden",
                  borderRadius: "8px 8px 0px 0px",
                  border: "1px solid rgba(0,0,0,0.4)",
                  borderLeft: "0",
                },
              },
              headRow: {
                style: {
                  background: "#b1c7e9",
                  color: "#3D3D3D",
                  fontWeight: "600",
                  "&:hover": {
                    cursor: "pointer",
                  },
                },
              },
              headCells: {
                style: {
                  border: "1px solid rgba(0,0,0,0.4)",
                  borderTop: "0",
                  borderRight: "0",
                  borderBottom: "0",
                },
              },
              cells: {
                style: {
                  borderLeft: "1px solid rgba(0,0,0,0.4)",
                },
              },
              rows: {
                style: {
                  backgroundColor: "#fff",
                  cursor: "default",
                  color: "#656363",
                  fontSize: "14px",
                  "&:hover": {
                    cursor: "default",
                    backgroundColor: "#E9E9E9",
                  },
                  borderBottom: "1px solid rgba(0,0,0,0.4)",
                },
              },
              pagination: {
                style: {
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                  borderRadius: "0.375rem",
                  borderTopLeftRadius: "0px",
                  borderTopRightRadius: "0px",
                  border: "1px solid rgba(0,0,0,0.4)",
                  borderTop: "0",
                  fontWeight: "500",
                  color: "rgb(61, 61, 61)",
                },
              },
            }}
          />
        </div>
      )}
    </div>
  );
};

export default UserBulkUpload;
